import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { XMarkIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { smsService } from '../../services/smsService';
import { SmsConversation, SmsMessage } from '../../types/sms';
import LoadingSpinner from '../UI/LoadingSpinner';
import { formatApiErrorMessage } from '../../utils/formatApiError';
import { useAuth } from '../../contexts/AuthContext';

function formatFullTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function getDateLabel(iso: string): string {
  try {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function MsgStatusIcon({ status }: { status: string }) {
  if (status === 'delivered') return <span className="text-[10px] font-bold text-primary">✓✓</span>;
  if (status === 'sent') return <span className="text-[10px] text-muted-foreground">✓</span>;
  if (status === 'failed' || status === 'undelivered') {
    return <span className="text-[10px] text-destructive font-bold">✗</span>;
  }
  return <span className="text-[10px] text-muted-foreground">○</span>;
}

function outboundSenderLabel(msg: SmsMessage): string | null {
  if (msg.direction !== 'outbound') return null;
  if (msg.sent_by_agent_id) {
    const name = msg.sent_by_agent_name?.trim() || 'Staff';
    const role = msg.sent_by_agent_role;
    if (role === 'admin' || role === 'owner') return `Admin · ${name}`;
    if (role === 'agent') return `Agent · ${name}`;
    return `Sent by · ${name}`;
  }
  const step = msg.sequence_step_number != null ? Number(msg.sequence_step_number) : null;
  if (step === 1) return 'Campaign · Initial message';
  if (step != null && step > 1) return `Campaign · Follow-up ${step - 1}`;
  return 'Campaign · Automated';
}

export interface SmsConversationPopupProps {
  open: boolean;
  onClose: () => void;
  leadId: string;
  leadName?: string | null;
  companyName?: string | null;
  leadPhone?: string | null;
}

const SmsConversationPopup: React.FC<SmsConversationPopupProps> = ({
  open,
  onClose,
  leadId,
  leadName,
  companyName,
  leadPhone,
}) => {
  const { user } = useAuth();
  const [conversation, setConversation] = useState<SmsConversation | null>(null);
  const [messages, setMessages] = useState<SmsMessage[]>([]);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const isAssignedToMe =
    Boolean(user?.id) && conversation?.assigned_agent_id === user?.id;
  const needsAssign = !isAdmin && user?.role === 'agent' && !isAssignedToMe;

  const scrollToBottom = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, []);

  const loadMessages = useCallback(async (conversationId: string) => {
    const result = await smsService.getConversationMessages(conversationId, { limit: 50 });
    setMessages(result.messages);
    setHasMoreMessages(result.hasMore);
  }, []);

  const loadConversation = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setConversation(null);
    setMessages([]);
    try {
      const convo = await smsService.getConversationForLead(leadId);
      setConversation(convo);
      await loadMessages(convo.id);
    } catch (error: unknown) {
      setLoadError(formatApiErrorMessage(error, 'Failed to load SMS conversation'));
    } finally {
      setLoading(false);
    }
  }, [leadId, loadMessages]);

  useEffect(() => {
    if (!open) return;
    void loadConversation();
  }, [open, loadConversation]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open || loading) return;
    requestAnimationFrame(() => scrollToBottom());
  }, [open, loading, messages.length, scrollToBottom]);

  const loadOlderMessages = async () => {
    if (!conversation || loadingOlder || !hasMoreMessages || messages.length === 0) return;
    const oldest = messages[0];
    if (!oldest) return;

    setLoadingOlder(true);
    const container = scrollRef.current;
    const prevScrollHeight = container?.scrollHeight ?? 0;

    try {
      const result = await smsService.getConversationMessages(conversation.id, {
        limit: 50,
        before: oldest.id,
      });
      setMessages((prev) => {
        const existing = new Set(prev.map((m) => m.id));
        const older = result.messages.filter((m) => !existing.has(m.id));
        return [...older, ...prev];
      });
      setHasMoreMessages(result.hasMore);
      requestAnimationFrame(() => {
        if (!container) return;
        container.scrollTop += container.scrollHeight - prevScrollHeight;
      });
    } catch {
      toast.error('Failed to load older messages');
    } finally {
      setLoadingOlder(false);
    }
  };

  const handleAssignToMe = async () => {
    if (!conversation) return;
    setAssigning(true);
    try {
      await smsService.assignConversationToMe(conversation.id);
      setConversation((prev) =>
        prev
          ? {
              ...prev,
              assigned_agent_id: user?.id ?? prev.assigned_agent_id,
              assigned_agent_name: user?.name ?? prev.assigned_agent_name,
              status: 'assigned',
            }
          : prev
      );
      toast.success('Conversation assigned to you');
    } catch (error: unknown) {
      toast.error(formatApiErrorMessage(error, 'Failed to assign conversation'));
    } finally {
      setAssigning(false);
    }
  };

  const handleSend = async () => {
    if (!conversation || !draft.trim()) return;
    setSending(true);
    try {
      const sent = await smsService.sendReply(conversation.id, draft.trim());
      setMessages((prev) => (prev.some((m) => m.id === sent.id) ? prev : [...prev, sent]));
      setDraft('');
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      requestAnimationFrame(() => scrollToBottom());
    } catch (error: unknown) {
      toast.error(formatApiErrorMessage(error, 'Failed to send message'));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  if (!open) return null;

  const displayName =
    conversation?.lead_name || leadName || conversation?.lead_phone || leadPhone || 'Merchant';
  const displayCompany = conversation?.company_name || companyName;
  const displayPhone = conversation?.lead_phone || leadPhone;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overscroll-contain">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      <div
        className="relative flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
        style={{ height: 'min(85vh, 720px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4 shrink-0">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary shrink-0" />
              <h2 className="text-lg font-semibold text-foreground truncate">{displayName}</h2>
            </div>
            {displayCompany && (
              <p className="text-sm text-muted-foreground mt-0.5 truncate">{displayCompany}</p>
            )}
            {displayPhone && (
              <p className="text-xs text-muted-foreground mt-0.5">{displayPhone}</p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to={`/sms/inbox?leadId=${leadId}`}
              className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted transition-colors"
              onClick={onClose}
            >
              Go to Inbox
            </Link>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 hover:bg-muted text-muted-foreground"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {needsAssign && conversation && (
          <div className="flex items-center justify-between gap-3 border-b border-amber-200/60 bg-amber-50 dark:bg-amber-900/20 px-5 py-2.5 shrink-0">
            <span className="text-xs text-amber-800 dark:text-amber-300">
              Assign this thread to yourself before sending.
            </span>
            <button
              type="button"
              onClick={() => void handleAssignToMe()}
              disabled={assigning}
              className="rounded-full bg-primary/15 px-3 py-1 text-xs font-medium text-primary hover:bg-primary/25 disabled:opacity-50"
            >
              {assigning ? 'Assigning…' : 'Assign to me'}
            </button>
          </div>
        )}

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto overscroll-contain px-5 py-4 space-y-3 bg-background/40"
        >
          {loading && (
            <div className="flex justify-center py-16">
              <LoadingSpinner size="md" />
            </div>
          )}

          {!loading && loadError && (
            <div className="text-center py-12 space-y-3">
              <p className="text-sm text-destructive">{loadError}</p>
              <button
                type="button"
                onClick={() => void loadConversation()}
                className="text-sm font-medium text-primary hover:underline"
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !loadError && hasMoreMessages && (
            <div className="flex justify-center py-1">
              <button
                type="button"
                onClick={() => void loadOlderMessages()}
                disabled={loadingOlder}
                className="text-[11px] text-primary hover:underline disabled:opacity-50"
              >
                {loadingOlder ? 'Loading…' : 'Load older messages'}
              </button>
            </div>
          )}

          {!loading && !loadError && messages.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-12">
              No messages yet. Send the first SMS below.
            </div>
          )}

          {!loading &&
            !loadError &&
            messages.map((msg, idx) => {
              const prevMsg = messages[idx - 1];
              const showDateSep =
                !prevMsg || getDateLabel(msg.created_at) !== getDateLabel(prevMsg.created_at);
              return (
                <React.Fragment key={msg.id}>
                  {showDateSep && (
                    <div className="flex items-center gap-3 my-1">
                      <div className="flex-1 h-px bg-border" />
                      <span className="text-[11px] text-muted-foreground font-medium px-2">
                        {getDateLabel(msg.created_at)}
                      </span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                  )}
                  <div
                    className={`flex flex-col max-w-[85%] ${
                      msg.direction === 'outbound' ? 'ml-auto items-end' : 'items-start'
                    }`}
                  >
                    {msg.direction === 'outbound' && outboundSenderLabel(msg) && (
                      <span className="text-[10px] font-medium text-muted-foreground mb-1 px-1">
                        {outboundSenderLabel(msg)}
                      </span>
                    )}
                    <div
                      className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed shadow-sm ${
                        msg.direction === 'outbound'
                          ? 'bg-primary text-primary-foreground rounded-br-md'
                          : 'bg-card text-foreground border border-border rounded-bl-md'
                      }`}
                    >
                      {msg.body}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 px-1">
                      <span className="text-[10px] text-muted-foreground">
                        {formatFullTime(msg.created_at)}
                      </span>
                      {msg.direction === 'outbound' && msg.status && (
                        <MsgStatusIcon status={msg.status} />
                      )}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
        </div>

        {!loading && !loadError && conversation && (
          <div className="shrink-0 border-t border-border px-5 py-4">
            <div className="flex gap-2 items-end bg-muted rounded-2xl px-2 py-2">
              <textarea
                ref={textareaRef}
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  const ta = textareaRef.current;
                  if (ta) {
                    ta.style.height = 'auto';
                    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
                  }
                }}
                onKeyDown={handleKeyDown}
                rows={1}
                disabled={needsAssign}
                className="flex-1 px-3 py-2 outline-none resize-none text-sm text-foreground bg-transparent placeholder-muted-foreground disabled:opacity-50"
                placeholder="Type a message…"
              />
              <button
                type="button"
                onClick={() => void handleSend()}
                disabled={sending || !draft.trim() || needsAssign}
                className="rounded-xl px-5 py-2 bg-primary hover:opacity-90 text-primary-foreground text-sm font-semibold shrink-0 disabled:opacity-40"
              >
                {sending ? '…' : 'Send'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default SmsConversationPopup;
